import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SourcesPanel } from "./SourcesPanel.js";

interface AddSourcesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload?: (file: File, title?: string) => Promise<void>;
}

export function AddSourcesDialog({
  open,
  onOpenChange,
  onUpload,
}: AddSourcesDialogProps) {
  const [uploading, setUploading] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [externalPickerActive, setExternalPickerActive] = useState(false);
  const [panelBusy, setPanelBusy] = useState(false);

  const isBusy = uploading || externalPickerActive || panelBusy;

  useEffect(() => {
    if (!open && !isBusy) {
      setResetKey((key) => key + 1);
      setExternalPickerActive(false);
      setPanelBusy(false);
    }
  }, [open, isBusy]);

  // Radix modal layers can leave body pointer-events disabled; release while Google Picker is open.
  useEffect(() => {
    if (!externalPickerActive) return;

    const body = document.body;
    const previousPointerEvents = body.style.pointerEvents;
    body.style.pointerEvents = "auto";
    body.removeAttribute("data-scroll-locked");

    return () => {
      body.style.pointerEvents = previousPointerEvents;
    };
  }, [externalPickerActive]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && isBusy) return;
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modal={!externalPickerActive}>
      <DialogContent
        allowOutsideInteraction={externalPickerActive}
        className="flex max-h-[min(640px,90dvh)] flex-col overflow-hidden border-glass-border bg-[#111b35] text-white sm:max-w-[800px]"
        onPointerDownOutside={
          externalPickerActive ? undefined : (event) => event.preventDefault()
        }
        onInteractOutside={
          externalPickerActive ? undefined : (event) => event.preventDefault()
        }
        onFocusOutside={externalPickerActive ? undefined : (event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Add sources</DialogTitle>
          <DialogDescription className={`text-slate-400 ${pageDescriptionMobileHiddenClassName}`}>
            Files are processed and indexed for KM search. Supported: PDF, DOCX, PPTX,
            HTML, MD, CSV, XLSX.
          </DialogDescription>
        </DialogHeader>
        <SourcesPanel
          key={resetKey}
          compact
          className="min-h-0 flex-1"
          showHeader={false}
          uploading={uploading}
          autoUploadAfterDriveImport
          onExternalPickerActiveChange={setExternalPickerActive}
          onBusyChange={setPanelBusy}
          onUpload={async (items) => {
            if (!onUpload) return;

            setUploading(true);
            try {
              for (const { file, title } of items) {
                await onUpload(file, title);
              }
              handleOpenChange(false);
            } catch (err) {
              throw err instanceof Error ? err : new Error("Upload failed");
            } finally {
              setUploading(false);
            }
          }}
        />
        <DialogFooter className="shrink-0">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isBusy}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
